// ── QR Code Generator (pure JS, no deps) ──
// Generates scannable QR codes. Fixed: format info now encodes the actual mask used.

// ECC level bits in the format info: L=01, M=00, Q=11, H=10. Reed-Solomon
// below is sized for M (v1: 16 data / 10 EC codewords), so the format bits
// must say M — the old value 1 advertised L and made every code undecodable.
const ECC_LEVEL = 0; // M

// ── GF(256) arithmetic for Reed-Solomon ──
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(function initGF() {
  let x = 1;
  for (let i = 0; i < 255; i++) { GF_EXP[i] = x; GF_LOG[x] = i; x = (x << 1) ^ (x & 0x80 ? 0x11d : 0); }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255];
})();
function gfMul(a: number, b: number): number { return a === 0 || b === 0 ? 0 : GF_EXP[GF_LOG[a] + GF_LOG[b]]; }
function rsGenPoly(nsym: number): number[] {
  let g = [1];
  for (let i = 0; i < nsym; i++) { const ng = new Array(g.length + 1).fill(0); for (let j = 0; j < g.length; j++) { ng[j] ^= g[j]; ng[j + 1] ^= gfMul(g[j], GF_EXP[i]); } g = ng; }
  return g;
}
function rsEncode(data: number[], nsym: number): number[] {
  const gen = rsGenPoly(nsym);
  const res = new Array(data.length + nsym).fill(0);
  for (let i = 0; i < data.length; i++) res[i] = data[i];
  for (let i = 0; i < data.length; i++) { const c = res[i]; if (c) for (let j = 1; j < gen.length; j++) res[i + j] ^= gfMul(gen[j], c); }
  return res.slice(data.length);
}

const VERSION_TABLE: Record<number, [number, number, number, number, number, number]> = {
  1: [26, 10, 1, 16, 0, 0], 2: [44, 16, 1, 28, 0, 0], 3: [70, 26, 1, 44, 0, 0],
  4: [100, 18, 2, 32, 0, 0], 5: [134, 24, 2, 43, 0, 0], 6: [172, 16, 4, 27, 0, 0],
  7: [196, 18, 4, 31, 0, 0], 8: [242, 22, 2, 38, 2, 39], 9: [292, 22, 3, 36, 2, 37],
  10: [346, 26, 4, 43, 1, 44], 11: [404, 30, 1, 50, 4, 51], 12: [466, 22, 6, 36, 2, 37],
  13: [532, 22, 8, 37, 1, 38], 14: [581, 24, 4, 40, 5, 41], 15: [655, 24, 5, 41, 5, 42],
  16: [733, 28, 7, 45, 3, 46], 17: [815, 28, 10, 46, 1, 47], 18: [901, 26, 9, 43, 4, 44],
  19: [991, 26, 3, 44, 11, 45], 20: [1085, 26, 3, 41, 13, 42],
};
const ALIGNMENT_PATTERNS: Record<number, number[]> = {
  2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34],
  7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
};

/** Alignment-pattern centre coordinates for any version (ISO/IEC 18004
 *  §8.7.2). The static table above stops at version 10; the version table
 *  goes to 20, so v11+ codes were silently generated with no alignment
 *  patterns and no decoder could read them. */
function alignmentPositions(version: number, size: number): number[] {
  if (version === 1) return [];
  if (ALIGNMENT_PATTERNS[version]) return ALIGNMENT_PATTERNS[version];
  const numAlign = Math.floor(version / 7) + 2;
  const step = version === 32 ? 26 : Math.ceil((version * 4 + 4) / (numAlign * 2 - 2)) * 2;
  const result: number[] = [6];
  for (let pos = size - 7; result.length < numAlign; pos -= step) result.splice(1, 0, pos);
  return result;
}

/** Smallest version that fits the payload, or null past the v20-M capacity
 *  (~665 bytes). Oversized input used to silently produce garbage. */
function getVersion(dataLen: number): number | null {
  for (let v = 1; v <= 20; v++) {
    const t = VERSION_TABLE[v];
    const dataCW = t[2] * t[3] + t[4] * t[5];
    const bits = 4 + (v <= 9 ? 8 : 16) + dataLen * 8;
    if (Math.ceil(bits / 8) <= dataCW) return v;
  }
  return null;
}

function encodeData(text: string, version: number): number[] {
  const t = VERSION_TABLE[version];
  const dataCW = t[2] * t[3] + t[4] * t[5];
  const bytes = new TextEncoder().encode(text);
  const bits: number[] = [];
  bits.push(0, 1, 0, 0); // byte mode
  const lenBits = version <= 9 ? 8 : 16;
  for (let i = lenBits - 1; i >= 0; i--) bits.push((bytes.length >> i) & 1);
  for (const b of bytes) for (let i = 7; i >= 0; i--) bits.push((b >> i) & 1);
  const totalBits = dataCW * 8;
  const tl = Math.min(4, totalBits - bits.length);
  for (let i = 0; i < tl; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);
  const pad = [0xec, 0x11]; let pi = 0;
  while (bits.length < totalBits) { const pb = pad[pi % 2]; for (let i = 7; i >= 0; i--) bits.push((pb >> i) & 1); pi++; }
  const cw: number[] = [];
  for (let i = 0; i < bits.length; i += 8) { let v = 0; for (let j = 0; j < 8; j++) v = (v << 1) | (bits[i + j] || 0); cw.push(v); }
  return cw;
}

function buildCodewords(data: number[], version: number): number[] {
  const t = VERSION_TABLE[version];
  const [, ep, g1, g1d, g2, g2d] = t;
  const blocks: number[][] = []; const ecBlocks: number[][] = []; let off = 0;
  for (let i = 0; i < g1; i++) { const b = data.slice(off, off + g1d); blocks.push(b); ecBlocks.push(rsEncode(b, ep)); off += g1d; }
  for (let i = 0; i < g2; i++) { const b = data.slice(off, off + g2d); blocks.push(b); ecBlocks.push(rsEncode(b, ep)); off += g2d; }
  const result: number[] = [];
  const md = Math.max(g1d, g2d || 0);
  for (let i = 0; i < md; i++) for (const b of blocks) if (i < b.length) result.push(b[i]);
  for (let i = 0; i < ep; i++) for (const e of ecBlocks) if (i < e.length) result.push(e[i]);
  return result;
}

// Matrix helpers
function makeMatrix(version: number) {
  const s = version * 4 + 17;
  return { m: Array.from({ length: s }, () => new Array(s).fill(null)), r: Array.from({ length: s }, () => new Array(s).fill(false)), s };
}
function setMod(m: (boolean | null)[][], r: boolean[][], row: number, col: number, val: boolean) {
  if (row >= 0 && row < m.length && col >= 0 && col < m[0].length) { m[row][col] = val; r[row][col] = true; }
}
function placeFinder(m: (boolean | null)[][], r: boolean[][], row: number, col: number) {
  for (let dr = -1; dr <= 7; dr++) for (let dc = -1; dc <= 7; dc++) {
    const rr = row + dr, cc = col + dc;
    if (rr < 0 || rr >= m.length || cc < 0 || cc >= m[0].length) continue;
    const o = dr >= 0 && dr <= 6 && dc >= 0 && dc <= 6;
    const i = dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4;
    setMod(m, r, rr, cc, i || (o && (dr === 0 || dr === 6 || dc === 0 || dc === 6)));
  }
}
function placeAlign(m: (boolean | null)[][], r: boolean[][], row: number, col: number) {
  for (let dr = -2; dr <= 2; dr++) for (let dc = -2; dc <= 2; dc++)
    setMod(m, r, row + dr, col + dc, Math.abs(dr) === 2 || Math.abs(dc) === 2 || (dr === 0 && dc === 0));
}
function placeTiming(m: (boolean | null)[][], r: boolean[][], size: number) {
  for (let i = 8; i < size - 8; i++) { const v = i % 2 === 0; if (!r[6][i]) setMod(m, r, 6, i, v); if (!r[i][6]) setMod(m, r, i, 6, v); }
}
function placeDark(m: (boolean | null)[][], r: boolean[][], version: number) { setMod(m, r, 4 * version + 9, 8, true); }
/** Version information (ISO/IEC 18004 §8.4) — required for version >= 7.
 *  18-bit BCH(18,6); two copies, LSB first. Without it every v7+ code is
 *  undecodable (payloads over ~100 bytes). */
function placeVersionInfo(m: (boolean | null)[][], r: boolean[][], version: number, size: number) {
  if (version < 7) return;
  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1F25);
  const bits = (version << 12) | rem;
  for (let i = 0; i < 18; i++) {
    const bit = ((bits >> i) & 1) === 1;
    const a = Math.floor(i / 3), b = size - 11 + (i % 3);
    setMod(m, r, b, a, bit); // bottom-left block (3x6)
    setMod(m, r, a, b, bit); // top-right block (6x3)
  }
}
function reserveFormat(r: boolean[][], size: number) {
  for (let i = 0; i <= 8; i++) { r[8][i] = true; r[i][8] = true; }
  for (let i = size - 8; i < size; i++) r[8][i] = true;
  for (let i = size - 7; i < size; i++) r[i][8] = true;
}

function placeData(m: (boolean | null)[][], r: boolean[][], data: number[], size: number) {
  let bi = 0; const tb = data.length * 8;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) for (let j = 0; j < 2; j++) {
      const col = right - j; const upward = ((right + 1) & 2) === 0; const row = upward ? size - 1 - vert : vert;
      if (r[row][col]) continue;
      // Write m directly — setMod would mark data modules as reserved in r,
      // and applyMask skips reserved modules, so the mask never got applied
      // while the format info still claimed it did. Every code was undecodable.
      if (bi < tb) { m[row][col] = ((data[bi >> 3] >> (7 - (bi & 7))) & 1) === 1; bi++; }
      else m[row][col] = false;
    }
  }
}

function applyMask(m: (boolean | null)[][], r: boolean[][], size: number, mask: number) {
  for (let row = 0; row < size; row++) for (let col = 0; col < size; col++) {
    if (r[row][col]) continue; let inv = false;
    switch (mask) {
      case 0: inv = (row + col) % 2 === 0; break; case 1: inv = row % 2 === 0; break;
      case 2: inv = col % 3 === 0; break; case 3: inv = (row + col) % 3 === 0; break;
      case 4: inv = (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0; break;
      case 5: inv = (row * col) % 2 + (row * col) % 3 === 0; break;
      case 6: inv = ((row * col) % 2 + (row * col) % 3) % 2 === 0; break;
      case 7: inv = ((row + col) % 2 + (row * col) % 3) % 2 === 0; break;
    }
    if (inv && m[row][col] !== null) m[row][col] = !m[row][col];
  }
}

// FIXED (2026-10-12): format-info module positions were transposed — bits
// were laid out LSB-first along the wrong axis, so every generated code had
// unreadable format info and no scanner could decode it (verified against
// jsQR: 0/N decoded before, N/N after). Positions now match ISO/IEC 18004
// via the standard nayuki ordering, bit 0 = LSB.
function placeFormat(m: (boolean | null)[][], size: number, mask: number) {
  const fi = (ECC_LEVEL << 3) | mask;
  let bits = fi << 10; let gen = 0x537;
  for (let i = 4; i >= 0; i--) if (bits & (1 << (i + 10))) bits ^= gen << i;
  const full = ((fi << 10) | bits) ^ 0x5412;
  const bit = (i: number) => ((full >> i) & 1) === 1;

  // First copy: LSB up the left column of the top-left finder, then across.
  const pos1 = [[0,8],[1,8],[2,8],[3,8],[4,8],[5,8],[7,8],[8,8],[8,7],[8,5],[8,4],[8,3],[8,2],[8,1],[8,0]];
  for (let i = 0; i < 15; i++) m[pos1[i][0]][pos1[i][1]] = bit(i);

  // Second copy: bits 0-7 along the right row, bits 8-14 down the bottom column.
  const pos2 = [[8,size-1],[8,size-2],[8,size-3],[8,size-4],[8,size-5],[8,size-6],[8,size-7],[8,size-8],[size-7,8],[size-6,8],[size-5,8],[size-4,8],[size-3,8],[size-2,8],[size-1,8]];
  for (let i = 0; i < 15; i++) m[pos2[i][0]][pos2[i][1]] = bit(i);
}

function scoreMask(m: (boolean | null)[][], size: number): number {
  let sc = 0;
  for (let row = 0; row < size; row++) { let run = 1; for (let col = 1; col < size; col++) { if (m[row][col] === m[row][col - 1]) { run++; } else { if (run >= 5) sc += run - 2; run = 1; } } if (run >= 5) sc += run - 2; }
  for (let col = 0; col < size; col++) { let run = 1; for (let row = 1; row < size; row++) { if (m[row][col] === m[row - 1][col]) { run++; } else { if (run >= 5) sc += run - 2; run = 1; } } if (run >= 5) sc += run - 2; }
  for (let row = 0; row < size - 1; row++) for (let col = 0; col < size - 1; col++) { const v = m[row][col]; if (v === m[row][col + 1] && v === m[row + 1][col] && v === m[row + 1][col + 1]) sc += 3; }
  return sc;
}

export function generateQRMatrix(text: string): boolean[][] {
  const version = getVersion(new TextEncoder().encode(text).length);
  if (version === null) throw new RangeError('QR payload too long (max ~665 bytes at ECC M)');
  const data = encodeData(text, version);
  const codewords = buildCodewords(data, version);
  const { m, r, s: size } = makeMatrix(version);

  placeFinder(m, r, 0, 0); placeFinder(m, r, 0, size - 7); placeFinder(m, r, size - 7, 0);
  placeTiming(m, r, size); placeDark(m, r, version); reserveFormat(r, size);

  const alignPos = alignmentPositions(version, size);
  // Place every alignment pattern except the three that collide with finder
  // patterns. The old `if (!r[row][col])` guard also skipped the patterns that
  // sit on the timing row/column (v7+: centres like (6,22)) — those ARE valid
  // patterns (their modules coincide with the timing alternation), so v7+
  // codes came out with holes in the function grid and data poured into them:
  // undecodable above ~106 bytes.
  for (const row of alignPos) for (const col of alignPos) {
    if ((row === 6 && col === 6) || (row === 6 && col === size - 7) || (row === size - 7 && col === 6)) continue;
    placeAlign(m, r, row, col);
  }

  placeVersionInfo(m, r, version, size);

  placeData(m, r, codewords, size);

  // Pick best mask, THEN apply it with correct format info
  let bestMask = 0, bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const masked = m.map(row => [...row]);
    applyMask(masked, r, size, mask);
    placeFormat(masked, size, mask); // <-- mask param matches actual mask
    const s = scoreMask(masked, size);
    if (s < bestScore) { bestScore = s; bestMask = mask; }
  }

  // Apply best mask with correct format info
  applyMask(m, r, size, bestMask);
  placeFormat(m, size, bestMask); // <-- FIXED: uses bestMask, not hardcoded 0

  return m.map(row => row.map(v => v === true));
}

export function qrToSVG(matrix: boolean[][], opts?: { size?: number; margin?: number; darkColor?: string; lightColor?: string }): string {
  const { size: cellSize = 8, margin: marginModules = 4, darkColor = '#0b0b0d', lightColor = '#ffffff' } = opts || {};
  const n = matrix.length;
  const margin = marginModules * cellSize;
  const totalSize = n * cellSize + margin * 2;
  let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalSize} ${totalSize}" width="${totalSize}" height="${totalSize}" shape-rendering="crispEdges">`;
  svg += `<rect width="${totalSize}" height="${totalSize}" fill="${lightColor}"/>`;
  for (let row = 0; row < n; row++) for (let col = 0; col < n; col++) {
    if (matrix[row][col]) svg += `<rect x="${margin + col * cellSize}" y="${margin + row * cellSize}" width="${cellSize}" height="${cellSize}" fill="${darkColor}"/>`;
  }
  svg += '</svg>';
  return svg;
}

export function qrToImgTag(matrix: boolean[][], opts?: { size?: number; margin?: number }): string {
  const svg = qrToSVG(matrix, opts);
  const b64 = btoa(svg);
  return `<img src="data:image/svg+xml;base64,${b64}" alt="QR Code" style="display:block" />`;
}
